/**
 * LeaderboardCelebration - Dramatic full-screen celebration for leaderboard achievements
 * Gaming-style animation when someone reaches top 3
 */

import { useState, useEffect } from 'react';
import { Trophy, Star, X, Crown, Medal, Flame } from 'lucide-react';

const LeaderboardCelebration = ({ alert, onClose }) => {
  const [isAnimating, setIsAnimating] = useState(true);
  const [showContent, setShowContent] = useState(false);

  // Get rank info from metadata
  const newRank = alert.metadata?.new_rank || 1;
  const oldRank = alert.metadata?.old_rank || 999;

  // Rank-specific styling
  const rankStyles = {
    1: {
      color: 'text-yellow-400',
      bgGlow: 'from-yellow-500/30 via-orange-500/20 to-yellow-500/30',
      icon: Crown,
      label: 'CHAMPION',
      particle: '👑',
    },
    2: {
      color: 'text-gray-300',
      bgGlow: 'from-gray-400/30 via-gray-500/20 to-gray-400/30',
      icon: Medal,
      label: 'RUNNER UP',
      particle: '🥈',
    },
    3: {
      color: 'text-orange-400',
      bgGlow: 'from-orange-500/30 via-red-500/20 to-orange-500/30',
      icon: Medal,
      label: 'THIRD PLACE',
      particle: '🥉',
    },
  };

  const style = rankStyles[newRank] || rankStyles[1];
  const RankIcon = style.icon;

  // Generate celebration particles with useState initializer to avoid impure function in useMemo
  const [particles] = useState(() => {
    const newParticles = [];
    const emojis = ['🏆', '⭐', '🎉', '🔥', style.particle, '💪', '🏈'];
    
    for (let i = 0; i < 30; i++) {
      newParticles.push({
        id: i,
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
        size: 1 + Math.random() * 1.5,
      });
    }
    return newParticles;
  });

  // Animation timing
  useEffect(() => {
    // Show content after initial animation
    const showTimer = setTimeout(() => setShowContent(true), 300);
    
    // Auto-close after 8 seconds
    const closeTimer = setTimeout(() => {
      setIsAnimating(false);
      setTimeout(onClose, 500);
    }, 8000);
    
    return () => {
      clearTimeout(showTimer);
      clearTimeout(closeTimer);
    };
  }, [onClose]);

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
      data-testid="leaderboard-celebration"
    >
      {/* Backdrop with animated gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${style.bgGlow} backdrop-blur-md`}>
        <div className="absolute inset-0 bg-black/70"></div>
        
        {/* Animated grid background */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50 animate-pulse"></div>
      </div>
      
      {/* Celebration Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map(p => (
          <div
            key={p.id}
            className="absolute animate-fall"
            style={{
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              fontSize: `${p.size}rem`,
            }}
          >
            {p.emoji}
          </div>
        ))}
      </div>
      
      {/* Close Button */}
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white/70 hover:text-white hover:bg-black/70 transition-all"
      >
        <X className="w-6 h-6" />
      </button>
      
      {/* Main Content */}
      <div className={`relative z-10 text-center transition-all duration-700 ${showContent ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}>
        {/* Trophy Icon with Glow */}
        <div className="relative inline-block mb-6">
          <div className={`absolute inset-0 ${style.color} blur-3xl opacity-50 animate-pulse`}>
            <Trophy className="w-32 h-32" />
          </div>
          <RankIcon className={`w-32 h-32 ${style.color} relative animate-bounce-slow`} />
        </div>
        
        {/* Rank Badge */}
        <div className={`inline-block px-6 py-2 rounded-full bg-black/50 border-2 border-current ${style.color} mb-4`}>
          <span className="font-orbitron text-xl tracking-widest">{style.label}</span>
        </div>
        
        {/* Title */}
        <h1 className="font-graffiti text-4xl md:text-6xl text-white mb-4 animate-glow">
          {alert.title}
        </h1>
        
        {/* Rank Change */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/30">
            <span className="font-orbitron text-red-400 text-lg">#{oldRank}</span>
          </div>
          <Flame className="w-8 h-8 text-orange-500 animate-pulse" />
          <div className="px-4 py-2 rounded-lg bg-green-500/20 border border-green-500/30">
            <span className={`font-orbitron ${style.color} text-2xl font-bold`}>#{newRank}</span>
          </div>
        </div>
        
        {/* Message */}
        <p className="font-rajdhani text-xl text-gray-300 max-w-md mx-auto">
          {alert.message}
        </p>
        
        {/* Stars decoration */}
        <div className="flex justify-center gap-2 mt-6">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className={`w-6 h-6 ${style.color} animate-twinkle`}
              style={{ animationDelay: `${i * 0.2}s` }}
              fill="currentColor"
            />
          ))}
        </div>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-fall {
          animation: fall linear forwards;
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        @keyframes glow {
          0%, 100% {
            text-shadow: 0 0 20px rgba(255, 255, 255, 0.5),
                         0 0 40px rgba(255, 255, 255, 0.3),
                         0 0 60px rgba(255, 255, 255, 0.2);
          }
          50% {
            text-shadow: 0 0 40px rgba(255, 255, 255, 0.8),
                         0 0 80px rgba(255, 255, 255, 0.5),
                         0 0 120px rgba(255, 255, 255, 0.3);
          }
        }
        
        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }
        
        @keyframes twinkle {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(0.8);
          }
        }
        
        .animate-twinkle {
          animation: twinkle 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LeaderboardCelebration;
